import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';
import useDebounce from './useDebounce';
import { apiDeduplicator } from '../utils/requestDeduplicator';

/**
 * Smart search hook that combines client-side and server-side search
 * Optimized for POS systems with large datasets
 */
const useSmartSearch = (endpoint, options = {}) => {
    const {
        searchFields = ['name', 'sku'], // Fields to search in
        minSearchLength = 2,            // Minimum characters before searching
        debounceDelay = 500,            // Debounce delay for server search
        cacheTimeout = 5 * 60 * 1000,   // Cache timeout (5 minutes)
        preloadActive = true,           // Preload active/frequently used items
        branchId = null,                // Branch-specific filtering
        categoryId = null               // Category filtering
    } = options;

    // State management
    const [searchTerm, setSearchTerm] = useState('');
    const [cachedData, setCachedData] = useState([]);
    const [serverResults, setServerResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [lastFetch, setLastFetch] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    // Debounced search term for server requests
    const debouncedSearchTerm = useDebounce(searchTerm, debounceDelay);

    // Check if cache is still valid
    const isCacheValid = useMemo(() => {
        if (!lastFetch) return false;
        return Date.now() - lastFetch < cacheTimeout;
    }, [lastFetch, cacheTimeout]);

    // Client-side search function
    const searchLocal = useCallback((term, data) => {
        if (!term || term.length < minSearchLength) return data;

        const searchLower = term.toLowerCase();
        return data.filter(item => {
            return searchFields.some(field => {
                const value = item[field];
                if (typeof value === 'string') {
                    return value.toLowerCase().includes(searchLower);
                }
                return false;
            });
        });
    }, [searchFields, minSearchLength]);

    // Load initial/cached data with request deduplication
    const loadCachedData = useCallback(async (force = false) => {
        if (!endpoint) return;

        // Create unique request key
        const requestKey = `${endpoint}_${branchId || 'all'}_${categoryId || 'all'}_${preloadActive}`;
        
        // If not forcing and request is already pending, wait for it
        if (!force && apiDeduplicator.isPending(requestKey)) {
            try {
                return await apiDeduplicator.execute(requestKey, () => Promise.resolve());
            } catch (err) {
                // If the pending request fails, we'll retry below
                console.warn('Pending request failed, retrying');
            }
        }

        return apiDeduplicator.execute(requestKey, async () => {
            try {
                setLoading(true);
                setError('');

                const params = {};
                
                if (preloadActive) {
                    // Load active products with inventory > 0
                    params.hasInventory = true;
                    params.status = 'active';
                }
                
                if (branchId) {
                    params.branchId = branchId;
                }
                
                if (categoryId) {
                    params.categoryId = categoryId;
                }

                const response = await api.get(endpoint, { params });
                const data = response.data.products || response.data.categories || response.data.inventory || response.data;
                
                setCachedData(Array.isArray(data) ? data : []);
                setLastFetch(Date.now());
                setIsInitialized(true);
                setError('');
                
                return data;
            } catch (err) {
                console.error('Error loading cached data:', err);
                setError(err.response?.status === 403 ? 'Access denied' : 'Failed to load data');
                setCachedData([]);
            } finally {
                setLoading(false);
            }
        });
    }, [endpoint, preloadActive, branchId, categoryId]);

    // Server search for items not in cache
    const searchServer = useCallback(async (term) => {
        if (!endpoint || !term || term.length < minSearchLength) {
            setServerResults([]);
            return;
        }

        const searchKey = `search_${endpoint}_${term}_${branchId || 'all'}_${categoryId || 'all'}`;
        
        try {
            const data = await apiDeduplicator.execute(searchKey, async () => {
                setLoading(true);
                setError('');

                const params = {
                    search: term,
                    branchId: branchId || undefined,
                    categoryId: categoryId || undefined,
                    limit: 50 // Limit server results
                };

                const response = await api.get(endpoint, { params });
                return response.data.products || response.data.categories || response.data.inventory || response.data;
            });
            
            setServerResults(Array.isArray(data) ? data : []);
            setError('');
        } catch (err) {
            console.error('Error searching server:', err);
            setError('Search failed');
            setServerResults([]);
        } finally {
            setLoading(false);
        }
    }, [endpoint, minSearchLength, branchId, categoryId]);

    // Combined search results
    const searchResults = useMemo(() => {
        if (!searchTerm || searchTerm.length < minSearchLength) {
            return cachedData;
        }

        // First, search cached data
        const localResults = searchLocal(searchTerm, cachedData);

        // If we have good local results, use them
        if (localResults.length > 0) {
            return localResults;
        }

        // Otherwise, use server results (if any)
        return serverResults;
    }, [searchTerm, cachedData, serverResults, searchLocal, minSearchLength]);

    // Single effect to handle all data loading logic
    useEffect(() => {
        const shouldInitialize = !isInitialized && !isCacheValid;
        const shouldRefresh = isInitialized && (!isCacheValid || cachedData.length === 0);
        
        if (shouldInitialize || shouldRefresh) {
            loadCachedData();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialized, isCacheValid, branchId, categoryId]);

    // Separate effect for search behavior
    useEffect(() => {
        if (debouncedSearchTerm && debouncedSearchTerm.length >= minSearchLength && isInitialized) {
            // Only search server if local search returned no results
            const localResults = searchLocal(debouncedSearchTerm, cachedData);
            if (localResults.length === 0) {
                searchServer(debouncedSearchTerm);
            } else {
                // Clear server results if we have local results
                setServerResults([]);
            }
        } else {
            setServerResults([]);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, minSearchLength, isInitialized]);

    return {
        searchTerm,
        setSearchTerm,
        results: searchResults,
        loading,
        error,
        isSearchPending: searchTerm !== debouncedSearchTerm,
        refreshCache: () => loadCachedData(true), // Force refresh
        cacheSize: cachedData.length,
        isUsingCache: searchResults === cachedData || searchLocal(searchTerm, cachedData).length > 0
    };
};

export default useSmartSearch;